import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, MeshTransmissionMaterial, Html, useProgress } from '@react-three/drei';

function Model() {
  const { nodes } = useGLTF('/Rayban.glb'); // Load the GLTF file and access nodes

  return (
    <group scale={[0.001, 0.001, 0.001]} position={[0, 0, 0]}>
      {/* Render the Lens_1 node with MeshTransmissionMaterial */}
      <mesh
        geometry={nodes.Lens_1.geometry}
        position={nodes.Lens_1.position}
        rotation={nodes.Lens_1.rotation}
        scale={nodes.Lens_1.scale}
        castShadow
      >
        <MeshTransmissionMaterial
          backside
          samples={4}
          thickness={3}
          chromaticAberration={0.025}
          anisotropy={0.1}
          distortion={0.1}
          distortionScale={0.1}
          temporalDistortion={0.2}
          iridescence={1}
          iridescenceIOR={1}
          iridescenceThicknessRange={[0, 1400]}
        />
      </mesh>

      {/* Render all other nodes as they are */}
      {Object.entries(nodes).map(([name, node]) => {
        if (name !== 'Lens_1' && node.isMesh) {
          return (
            <mesh
              key={name}
              geometry={node.geometry}
              material={node.material}
              position={node.position}
              rotation={node.rotation}
              scale={node.scale}
              castShadow
            />
          );
        }
        return null;
      })}
    </group>
  );
}

function LoadingBar() {
  const { progress } = useProgress(); // Track loading progress

  return progress < 100 ? (
    <Html center>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: '#fff',
          padding: '20px',
          borderRadius: '5px',
          zIndex: 10,
          textAlign: 'center',
        }}
      >
        <p>Loading... {Math.round(progress)}%</p>
        <div
          style={{
            width: '200px',
            height: '10px',
            backgroundColor: '#ccc',
            borderRadius: '5px',
            overflow: 'hidden',
            marginTop: '10px',
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              backgroundColor: '#007BFF',
            }}
          ></div>
        </div>
      </div>
    </Html>
  ) : null; // Only render loading bar if progress < 100
}

function Configurator() {
  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        background: '#ffffff',
        overflow: 'hidden',
      }}
    >
      <Canvas
        shadows
        camera={{ position: [25, 25, 25], fov: 75 }}
        onCreated={({ gl }) => {
          gl.setClearColor('#ffffff'); // White canvas background
        }}
      >
        {/* Suspense to handle loading */}
        <Suspense fallback={<LoadingBar />}>
          {/* Render the Model */}
          <Model />

          {/* Lighting */}
          <ambientLight intensity={1} /> {/* Increased ambient light */}
          <directionalLight position={[10, 10, 10]} intensity={2} /> {/* Increased intensity */}
          <directionalLight position={[-10, -10, -10]} intensity={1.5} />

          {/* OrbitControls for navigation */}
          <OrbitControls enableZoom={true} enablePan={false} makeDefault />

          {/* Reflective Environment */}
          <Environment preset="warehouse" background />
        </Suspense>
      </Canvas>
    </div>
  );
}

export default Configurator;
